import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, Trash2, Save, ArrowUp, ArrowDown, Camera, HeartHandshake } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { RichTextEditor } from '../../components/RichTextEditor';
import { ImageField } from './AdminCrud';
import { SupportBadge } from './AdminStoryMedia';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const SUPPORT_LABELS = {
  'resume-supported': 'Resume-supported',
  'flyer-supported': 'Flyer-supported',
  'steve-provided': 'Steve-provided',
  'document-attached': 'Proof attached',
  'needs-document': 'Needs document',
  'private-source': 'Private source only',
};

const PAGE_LABELS = {
  home: 'Home', 'meet-steve': 'Meet Steve', 'why-im-running': "Why I'm Running", platform: 'Platform',
  'ward-8': 'Ward 8', 'action-hub': 'Ward 8 Public Issue Tracker', experience: "Steve's Experience", 'whats-happening': "What's Happening",
};

const newId = () => `sec-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState('home');
  const [page, setPage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/pages').then((r) => setPages(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setPage(null);
    api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage({ slug, title: '', sections: [] }));
  }, [slug]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/pages/${slug}`, page);
      toast.success('Page saved and published');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const setField = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setSection = (i, k, v) => setPage((p) => ({ ...p, sections: p.sections.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const setListItem = (listKey, i, k, v) => setPage((p) => ({ ...p, [listKey]: p[listKey].map((item, j) => (j === i ? { ...item, [k]: v } : item)) }));

  return (
    <div data-testid="admin-pages">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Pages</h1>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="pages-supporter-wall-link">
            <Link to="/admin/supporters"><HeartHandshake className="h-4 w-4 mr-2" /> Supporter Wall</Link>
          </Button>
          {page?.slug === 'experience' && (
            <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="pages-story-media-link">
              <Link to="/admin/story-media"><Camera className="h-4 w-4 mr-2" /> Story Media Manager</Link>
            </Button>
          )}
          <Button onClick={save} disabled={busy || !page} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="pages-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Page
          </Button>
        </div>
      </div>

      <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3 mb-5">
        <label className="block text-xs font-bold text-[#1a2744] mb-1.5">Select page to edit</label>
        <Select value={slug} onValueChange={setSlug}>
          <SelectTrigger className="h-11 bg-white max-w-sm" data-testid="pages-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(pages.length ? pages.map((p) => p.slug) : Object.keys(PAGE_LABELS)).map((s) => (
              <SelectItem key={s} value={s}>{PAGE_LABELS[s] || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!page ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-5">
          <Card><CardContent className="p-5 space-y-4">
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Page title</label>
              <Input value={page.title || ''} onChange={(e) => setField('title', e.target.value)} className="h-11" data-testid="pages-title-input" />
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Subtitle</label>
              <Input value={page.subtitle || ''} onChange={(e) => setField('subtitle', e.target.value)} className="h-11" data-testid="pages-subtitle-input" />
            </div>
            {'hero_image' in page || ['meet-steve', 'why-im-running', 'ward-8'].includes(slug) ? (
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Header image</label>
                <ImageField value={page.hero_image} onChange={(v) => setField('hero_image', v)} category="pages" />
              </div>
            ) : null}
          </CardContent></Card>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif font-bold text-lg text-[#1a2744]">Content sections</h2>
              <Button variant="outline" size="sm" className="font-bold" data-testid="pages-add-section"
                onClick={() => setField('sections', [...(page.sections || []), { id: newId(), type: 'rich', kicker: '', title: 'New section', body_html: '', order: (page.sections?.length || 0), published: true }])}>
                <Plus className="h-4 w-4 mr-1" /> Add section
              </Button>
            </div>
            <Accordion type="multiple" className="space-y-3">
              {(page.sections || []).map((s, i) => (
                <AccordionItem key={s.id} value={s.id} className="bg-white rounded-xl border border-slate-200 px-4">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <span className="font-bold text-[#1a2744] text-sm text-left">{s.title || `Section ${i + 1}`} {s.published === false && <span className="text-slate-400 font-normal">(hidden)</span>}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Kicker (small label)</label>
                        <Input value={s.kicker || ''} onChange={(e) => setSection(i, 'kicker', e.target.value)} className="h-10" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Section title</label>
                        <Input value={s.title || ''} onChange={(e) => setSection(i, 'title', e.target.value)} className="h-10" />
                      </div>
                    </div>
                    {s.type === 'video_intro' && (
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Video embed URL (YouTube/Rumble/Vimeo embed link)</label>
                        <Input value={s.embed_url || ''} onChange={(e) => setSection(i, 'embed_url', e.target.value)} className="h-10" placeholder="https://www.youtube.com/embed/VIDEO_ID" />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Body</label>
                      <RichTextEditor value={s.body_html} onChange={(v) => setSection(i, 'body_html', v)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Tool links (optional — one per line: Label | URL, e.g. City Data | /city-data)</label>
                      <Textarea value={s.links_text || ''} onChange={(e) => setSection(i, 'links_text', e.target.value)} rows={3} data-testid={`pages-section-links-${i}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Callout line (optional — bold highlighted line, used by callout sections)</label>
                      <Input value={s.callout_text || ''} onChange={(e) => setSection(i, 'callout_text', e.target.value)} className="h-10" data-testid={`pages-section-callout-${i}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Support level (admin label)</label>
                      <select value={s.support_level || ''} onChange={(e) => setSection(i, 'support_level', e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid={`pages-section-support-${i}`}>
                        <option value="">— None —</option>
                        {Object.entries(SUPPORT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                        <Switch checked={s.published !== false} onCheckedChange={(v) => setSection(i, 'published', v)} /> Visible on site
                      </label>
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 font-bold" onClick={() => setField('sections', page.sections.filter((_, j) => j !== i))}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Platform pillars */}
          {page.pillars && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif font-bold text-lg text-[#1a2744]">Platform pillars</h2>
                <Button variant="outline" size="sm" className="font-bold" onClick={() => setField('pillars', [...page.pillars, { id: newId(), number: page.pillars.length + 1, title: 'New pillar', body: '' }])}><Plus className="h-4 w-4 mr-1" /> Add pillar</Button>
              </div>
              <div className="space-y-3">
                {page.pillars.map((p, i) => (
                  <Card key={p.id}><CardContent className="p-4 space-y-2">
                    <div className="flex gap-2">
                      <Input type="number" value={p.number} onChange={(e) => setListItem('pillars', i, 'number', parseInt(e.target.value) || 0)} className="h-10 w-20" />
                      <Input value={p.title} onChange={(e) => setListItem('pillars', i, 'title', e.target.value)} className="h-10 flex-1" />
                      <button onClick={() => setField('pillars', page.pillars.filter((_, j) => j !== i))} className="text-red-600 p-2" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <Textarea value={p.body} onChange={(e) => setListItem('pillars', i, 'body', e.target.value)} rows={2} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input value={p.link || ''} onChange={(e) => setListItem('pillars', i, 'link', e.target.value)} placeholder="Optional link (e.g. /paper-ballots)" className="h-9 text-sm" data-testid={`pillar-link-${p.id}`} />
                      <Input value={p.link_label || ''} onChange={(e) => setListItem('pillars', i, 'link_label', e.target.value)} placeholder="Link label (e.g. Read more)" className="h-9 text-sm" data-testid={`pillar-link-label-${p.id}`} />
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            </div>
          )}

          {/* Why vote cards */}
          {page.why_vote_cards && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif font-bold text-lg text-[#1a2744]">Why Vote Steve cards</h2>
                <Button variant="outline" size="sm" className="font-bold" onClick={() => setField('why_vote_cards', [...page.why_vote_cards, { id: newId(), title: 'New card', body: '' }])}><Plus className="h-4 w-4 mr-1" /> Add card</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {page.why_vote_cards.map((c, i) => (
                  <Card key={c.id}><CardContent className="p-4 space-y-2">
                    <div className="flex gap-2">
                      <Input value={c.title} onChange={(e) => setListItem('why_vote_cards', i, 'title', e.target.value)} className="h-10 flex-1" />
                      <button onClick={() => setField('why_vote_cards', page.why_vote_cards.filter((_, j) => j !== i))} className="text-red-600 p-2" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <Textarea value={c.body} onChange={(e) => setListItem('why_vote_cards', i, 'body', e.target.value)} rows={2} />
                  </CardContent></Card>
                ))}
              </div>
            </div>
          )}

          {/* Selected projects (Experience page) */}
          {page.projects && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif font-bold text-lg text-[#1a2744]">Selected projects</h2>
                <Button variant="outline" size="sm" className="font-bold" data-testid="pages-add-project"
                  onClick={() => setField('projects', [...page.projects, { id: newId(), title: 'New project', body: '', source_note: 'Steve-provided / supporting document optional', link_url: '', published: true }])}>
                  <Plus className="h-4 w-4 mr-1" /> Add project
                </Button>
              </div>
              <Card className="mb-3"><CardContent className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Projects section title</label>
                  <Input value={page.projects_title || ''} onChange={(e) => setField('projects_title', e.target.value)} className="h-10" data-testid="pages-projects-title" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Projects intro</label>
                  <Textarea value={page.projects_intro || ''} onChange={(e) => setField('projects_intro', e.target.value)} rows={2} data-testid="pages-projects-intro" />
                </div>
              </CardContent></Card>
              <Accordion type="multiple" className="space-y-3">
                {page.projects.map((p, i) => (
                  <AccordionItem key={p.id} value={p.id} className="bg-white rounded-xl border border-slate-200 px-4">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <span className="font-bold text-[#1a2744] text-sm text-left">{p.title || `Project ${i + 1}`} {p.published === false && <span className="text-slate-400 font-normal">(hidden)</span>}</span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 space-y-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Project title</label>
                        <Input value={p.title || ''} onChange={(e) => setListItem('projects', i, 'title', e.target.value)} className="h-10" data-testid={`pages-project-title-${i}`} />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Description</label>
                        <Textarea value={p.body || ''} onChange={(e) => setListItem('projects', i, 'body', e.target.value)} rows={3} data-testid={`pages-project-body-${i}`} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-1">Source note (e.g. Resume-supported / Steve-provided)</label>
                          <Input value={p.source_note || ''} onChange={(e) => setListItem('projects', i, 'source_note', e.target.value)} className="h-10" data-testid={`pages-project-source-${i}`} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-1">Related link URL (optional)</label>
                          <Input value={p.link_url || ''} onChange={(e) => setListItem('projects', i, 'link_url', e.target.value)} className="h-10" data-testid={`pages-project-link-${i}`} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                          <Switch checked={p.published !== false} onCheckedChange={(v) => setListItem('projects', i, 'published', v)} data-testid={`pages-project-show-${i}`} /> Visible on site
                        </label>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="font-bold" disabled={i === 0} data-testid={`pages-project-up-${i}`}
                            onClick={() => { const arr = [...page.projects]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setField('projects', arr); }}>
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" className="font-bold" disabled={i === page.projects.length - 1} data-testid={`pages-project-down-${i}`}
                            onClick={() => { const arr = [...page.projects]; [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; setField('projects', arr); }}>
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-200 font-bold" onClick={() => setField('projects', page.projects.filter((_, j) => j !== i))} data-testid={`pages-project-remove-${i}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Experience extras */}
          {page.skills && (
            <Card><CardContent className="p-5 space-y-4">
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Skills (one per line)</label>
                <Textarea value={page.skills.join('\n')} onChange={(e) => setField('skills', e.target.value.split('\n'))} rows={6} />
              </div>
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Experience highlights (one per line)</label>
                <Textarea value={(page.highlights || []).join('\n')} onChange={(e) => setField('highlights', e.target.value.split('\n'))} rows={6} />
              </div>
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Awards (one per line)</label>
                <Textarea value={(page.awards || []).map((a) => a.title).join('\n')} onChange={(e) => setField('awards', e.target.value.split('\n').map((t, i) => ({ id: `award-${i}`, title: t })))} rows={6} />
              </div>
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
